import { LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import {
  ADA_DERIVED_FROM,
  ada,
  daysPerWeek,
  delivery,
  every,
  itemKind,
  loincCode,
  T2DM_FOCUS_CODINGS,
  patient,
  taskCategory,
} from './builders';

export { T2DM_FOCUS_CODINGS };

export const T2DM_ONGOING_MEASUREMENTS_URL = 'https://liza.health/fhir/PlanDefinition/t2dm-ongoing-measurements';
export const T2DM_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const T2DM_ONGOING_MEASUREMENTS_CANONICAL = `${T2DM_ONGOING_MEASUREMENTS_URL}|${T2DM_ONGOING_MEASUREMENTS_VERSION}`;

/** The LOINC codes this protocol tracks, named from the shared `LOINC_CODES`. */
export const T2DM_ONGOING_CODES = {
  A1C: LOINC_CODES.HBA1C,
  BLOOD_PRESSURE: LOINC_CODES.BLOOD_PRESSURE_PANEL,
  BODY_WEIGHT: LOINC_CODES.BODY_WEIGHT,
  LDL: LOINC_CODES.LDL_CHOLESTEROL,
  HDL: LOINC_CODES.HDL_CHOLESTEROL,
  TRIGLYCERIDES: LOINC_CODES.TRIGLYCERIDES,
  TOTAL_CHOLESTEROL: LOINC_CODES.TOTAL_CHOLESTEROL,
  EGFR: LOINC_CODES.EGFR,
  CREATININE: LOINC_CODES.CREATININE,
  URINE_ALBUMIN_CREATININE_RATIO: LOINC_CODES.URINE_ALBUMIN_CREATININE_RATIO,
  FASTING_GLUCOSE: LOINC_CODES.FASTING_GLUCOSE,
} as const;

/** Every action here is an unconditional patient-owned measurement; only code, cadence and words differ. */
const measurement = (
  id: string,
  title: string,
  description: string,
  code: string,
  timing: Timing | undefined,
  recommendation: string,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  ...(timing ? { timingTiming: timing } : {}),
  documentation: ada(recommendation),
});

// --- actions ------------------------------------------------------------------------------------------

const a1c = measurement(
  'a1c',
  'Hemoglobin A1C',
  'A1C every 3 months.',
  T2DM_ONGOING_CODES.A1C,
  every(3, 'mo'),
  'Recommendations 6.1 and 6.2',
);

const bloodPressure = measurement(
  'blood-pressure',
  'Home blood pressure',
  'Home blood pressure monitoring on three days a week.',
  T2DM_ONGOING_CODES.BLOOD_PRESSURE,
  daysPerWeek(3),
  'Recommendations 10.1 and 10.2',
);

const bodyWeight = measurement(
  'body-weight',
  'Body weight',
  'Weekly weight.',
  T2DM_ONGOING_CODES.BODY_WEIGHT,
  every(1, 'wk'),
  'Recommendations 3.3 and 8.5',
);

const ldl = measurement(
  'ldl',
  'LDL cholesterol',
  'Lipid panel yearly.',
  T2DM_ONGOING_CODES.LDL,
  every(1, 'a'),
  'Recommendation 10.26',
);

const hdl = measurement(
  'hdl',
  'HDL cholesterol',
  'Lipid panel yearly.',
  T2DM_ONGOING_CODES.HDL,
  every(1, 'a'),
  'Recommendation 10.26',
);

const triglycerides = measurement(
  'triglycerides',
  'Triglycerides',
  'Lipid panel yearly.',
  T2DM_ONGOING_CODES.TRIGLYCERIDES,
  every(1, 'a'),
  'Recommendation 10.26',
);

const totalCholesterol = measurement(
  'total-cholesterol',
  'Total cholesterol',
  'Lipid panel yearly.',
  T2DM_ONGOING_CODES.TOTAL_CHOLESTEROL,
  every(1, 'a'),
  'Recommendation 10.26',
);

const egfr = measurement(
  'egfr',
  'eGFR',
  'Estimated glomerular filtration rate yearly, for kidney disease.',
  T2DM_ONGOING_CODES.EGFR,
  every(1, 'a'),
  'Recommendation 11.1',
);

const creatinine = measurement(
  'creatinine',
  'Creatinine',
  'Serum creatinine yearly, for kidney disease.',
  T2DM_ONGOING_CODES.CREATININE,
  every(1, 'a'),
  'Recommendation 11.1',
);

const urineAlbuminCreatinineRatio = measurement(
  'urine-albumin-creatinine-ratio',
  'Urine albumin-creatinine ratio',
  'Urine albumin-creatinine ratio yearly, for diabetic kidney disease.',
  T2DM_ONGOING_CODES.URINE_ALBUMIN_CREATININE_RATIO,
  every(1, 'a'),
  'Recommendation 11.1',
);

/**
 * No cadence: the ADA asks for self-monitoring on insulin or an agent that can cause hypoglycemia,
 * which this protocol has no way to express — it carries no conditions. Materialized as an as-needed
 * measurement so the metric is on the plan without asserting a schedule the guideline does not give.
 */
const fastingGlucose = measurement(
  'fasting-glucose',
  'Fasting glucose',
  'Fasting glucose, as needed.',
  T2DM_ONGOING_CODES.FASTING_GLUCOSE,
  undefined,
  'Recommendation 6.5',
);

// --- the protocol -------------------------------------------------------------------------------------

/**
 * The measurements the ADA asks for on an ongoing basis in type 2 diabetes. Unlike
 * `t2dm-routine-monitoring`, every action here is unconditional and patient-owned: this protocol
 * answers "what should be tracked" for a chart that has the diagnosis, and is expanded into
 * suggestions rather than committed as items.
 *
 * Diagnostic and mechanistic tests (C-peptide, GAD65, fasting insulin) are deliberately absent — they
 * are ordered once to clarify a diagnosis, not tracked over time.
 */
export const t2dmOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: T2DM_ONGOING_MEASUREMENTS_URL,
  version: T2DM_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: T2DM_ONGOING_MEASUREMENTS_URL }],
  name: 't2dm-ongoing-measurements',
  title: 'Type 2 diabetes — ongoing measurements',
  // Delivered as suggestions, never written straight onto the plan: this expands onto a freshly
  // bootstrapped chart, where nothing has been reviewed yet.
  extension: delivery('suggest'),
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-10',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in type 2 diabetes, expanded onto a care-plan topic when a type 2 diabetes Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...T2DM_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [ADA_DERIVED_FROM],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for type 2 diabetes.',
      action: [
        a1c,
        bloodPressure,
        bodyWeight,
        ldl,
        hdl,
        triglycerides,
        totalCholesterol,
        egfr,
        creatinine,
        urineAlbuminCreatinineRatio,
        fastingGlucose,
      ],
    },
  ],
};
